import { useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isWithinInterval,
  parseISO,
} from 'date-fns';

const overlap = (m1, m2) => {
  try {
    const s1 = parseISO(m1.fecha_inicio);
    const e1 = parseISO(m1.fecha_fin);
    const s2 = parseISO(m2.fecha_inicio);
    const e2 = parseISO(m2.fecha_fin);
    return s1 < e2 && s2 < e1;
  } catch {
    return false;
  }
};

export const useCalendarData = (movilizaciones, currentMonth) => {
  const days = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  const activeMovs = useMemo(
    () => movilizaciones.filter((m) => m.estado !== 'rechazado'),
    [movilizaciones]
  );

  const movsByDay = useMemo(() => {
    const map = new Map();
    for (const day of days) {
      map.set(format(day, 'yyyy-MM-dd'), []);
    }
    for (const mov of activeMovs) {
      try {
        const start = parseISO(mov.fecha_inicio);
        const end = parseISO(mov.fecha_fin);
        for (const day of days) {
          const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0);
          const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59);
          if (
            isWithinInterval(start, { start: dayStart, end: dayEnd }) ||
            isWithinInterval(end, { start: dayStart, end: dayEnd }) ||
            (start < dayStart && end > dayEnd)
          ) {
            map.get(format(day, 'yyyy-MM-dd')).push(mov);
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development' && typeof console !== 'undefined') {
          // eslint-disable-next-line no-console
          console.warn('CalendarView: invalid movilizacion dates', mov?.id, err?.message);
        }
      }
    }
    return map;
  }, [activeMovs, days]);

  const conflictsByDay = useMemo(() => {
    const conflicts = new Map();
    for (const [key, movs] of movsByDay.entries()) {
      let hasConflict = false;
      for (let i = 0; i < movs.length && !hasConflict; i++) {
        for (let j = i + 1; j < movs.length; j++) {
          if (overlap(movs[i], movs[j])) {
            hasConflict = true;
            break;
          }
        }
      }
      conflicts.set(key, hasConflict);
    }
    return conflicts;
  }, [movsByDay]);

  const totalConflictsInMonth = useMemo(() => {
    let count = 0;
    for (const day of days) {
      if (!isSameMonth(day, currentMonth)) continue;
      if (conflictsByDay.get(format(day, 'yyyy-MM-dd'))) count++;
    }
    return count;
  }, [conflictsByDay, days, currentMonth]);

  return { days, movsByDay, conflictsByDay, totalConflictsInMonth };
};
